import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useActivation from "@/hooks/useActivation";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Check, Circle, Loader2, Sparkles, ExternalLink } from "lucide-react";

// ============================================================================
// Constants
// ============================================================================
const ACTIVATION_STEPS = [
  { key: "intake", label: "Lead Intake Setup", description: "Ensure MissionAssist can receive leads from your traffic sources." },
  { key: "revenue", label: "Revenue Tracking Setup", description: "Ensure revenue is tracked and attributed correctly." },
  { key: "lead_routing", label: "Lead Routing Setup", description: "Define where new leads should be sent for follow-up." },
  { key: "appointment_sync", label: "Appointment Sync Setup", description: "Ensure booked appointments are returned to MissionAssist." },
];

const TRAFFIC_SOURCES = [
  { value: "paid_traffic", label: "Paid Traffic (Ads)" },
  { value: "organic_traffic", label: "Organic Traffic" },
  { value: "referral_traffic", label: "Referral Traffic" },
];

const PLATFORMS = [
  { value: "meta", label: "Meta" },
  { value: "google", label: "Google" },
  { value: "tiktok", label: "TikTok" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "other", label: "Other" },
];

const INTAKE_METHODS = [
  { value: "lead_form", label: "Lead Form" },
  { value: "landing_page", label: "Landing Page / Funnel" },
  { value: "api_webhook", label: "API / Webhook" },
];

const REVENUE_MODELS = [
  { value: "insurance", label: "Insurance (Commission Profiles)" },
  { value: "fixed_revenue", label: "Fixed Revenue" },
  { value: "variable_deal", label: "Variable Deal Size" },
  { value: "subscription", label: "Subscription (Coming Soon)", disabled: true },
];

const ROUTING_DESTINATION_TYPES = [
  { value: "crm", label: "CRM" },
  { value: "webhook", label: "Webhook / API" },
  { value: "other", label: "Other" },
];

const ROUTING_PROVIDERS = [
  { value: "ghl", label: "GHL" },
  { value: "hubspot", label: "HubSpot" },
  { value: "salesforce", label: "Salesforce" },
  { value: "ringy", label: "Ringy" },
  { value: "phoneburner", label: "PhoneBurner" },
  { value: "custom_webhook", label: "Custom Webhook" },
  { value: "other", label: "Other" },
];

const BOOKING_PROVIDERS = [
  { value: "calendly", label: "Calendly" },
  { value: "ghl_calendar", label: "GHL Calendar" },
  { value: "hubspot_meetings", label: "HubSpot Meetings" },
  { value: "acuity", label: "Acuity" },
  { value: "other", label: "Other" },
];

const RETURN_METHODS = [
  { value: "webhook", label: "Webhook (Recommended)" },
  { value: "native", label: "Native Integration" },
  { value: "zapier", label: "Zapier / Middleware" },
  { value: "manual_import", label: "Manual Import" },
  { value: "other", label: "Other" },
];

// ============================================================================
// Multi-select pill component
// ============================================================================
function MultiSelectPills({ options, selected, onChange, testIdPrefix }) {
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isSelected = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggleOption(opt.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              isSelected
                ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid={`${testIdPrefix}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Step 1: Lead Intake
// ============================================================================
function LeadIntakeStep({ capability, onSave, onSkip }) {
  const data = capability?.data || {};
  const [trafficSources, setTrafficSources] = useState(data.traffic_sources || []);
  const [platforms, setPlatforms] = useState(data.platforms || []);
  const [intakeMethods, setIntakeMethods] = useState(data.intake_methods || []);
  const [readiness, setReadiness] = useState(data.readiness || "");

  const isReady = readiness === "yes";
  const isNotReady = readiness === "not_yet";
  const canComplete = isReady;

  const handleSave = () => {
    onSave("intake", canComplete, {
      traffic_sources: trafficSources,
      platforms,
      intake_methods: intakeMethods,
      readiness,
    });
  };

  return (
    <div className="space-y-6" data-testid="intake-step-content">
      <div>
        <p className="text-sm text-slate-300 mb-1">Lead Intake Configuration Required</p>
        <p className="text-xs text-slate-500">MissionAssist requires an active lead source to operate. Define your traffic context and confirm intake readiness.</p>
      </div>

      {/* A. Traffic Sources */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">Traffic Sources</Label>
        <MultiSelectPills
          options={TRAFFIC_SOURCES}
          selected={trafficSources}
          onChange={setTrafficSources}
          testIdPrefix="intake-source"
        />
      </div>

      {/* B. Platforms */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">Platforms</Label>
        <MultiSelectPills
          options={PLATFORMS}
          selected={platforms}
          onChange={setPlatforms}
          testIdPrefix="intake-platform"
        />
      </div>

      {/* C. Intake Methods */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">Intake Methods</Label>
        <MultiSelectPills
          options={INTAKE_METHODS}
          selected={intakeMethods}
          onChange={setIntakeMethods}
          testIdPrefix="intake-method"
        />
      </div>

      {/* D. Readiness Confirmation */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">
          Are leads currently being sent into MissionAssist from these sources?
        </Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setReadiness("yes")}
            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
              readiness === "yes"
                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="intake-readiness-yes"
          >
            Yes, intake is active
          </button>
          <button
            type="button"
            onClick={() => setReadiness("not_yet")}
            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
              readiness === "not_yet"
                ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="intake-readiness-not-yet"
          >
            Not yet — needs configuration
          </button>
        </div>
      </div>

      {/* Not Ready instruction block */}
      {isNotReady && (
        <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-3" data-testid="intake-not-ready-block">
          <p className="text-xs text-amber-300 font-medium">
            Lead intake is not configured. MissionAssist cannot receive or process leads until this is completed.
          </p>
          <div className="space-y-1.5 text-[10px] text-slate-400">
            <p>1. Navigate to Settings &rarr; Integrations &rarr; Lead Intake</p>
            <p>2. Configure your webhook or connect your lead source</p>
            <p>3. Send a test lead to verify intake is operational</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
            onClick={() => window.open("/settings/integrations/intake", "_blank")}
            data-testid="intake-setup-cta"
          >
            <ExternalLink className="h-3 w-3 mr-1.5" />
            Go to Lead Intake Setup
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <button
          onClick={onSkip}
          className="text-xs text-slate-500 hover:text-slate-400 underline"
          data-testid="intake-skip-btn"
        >
          Continue Without Lead Intake
        </button>
        <Button
          onClick={handleSave}
          disabled={!canComplete}
          className={`text-xs ${
            canComplete
              ? "bg-indigo-600 hover:bg-indigo-700 text-white"
              : "bg-slate-800 text-slate-500 cursor-not-allowed"
          }`}
          data-testid="intake-complete-btn"
        >
          Complete & Continue
          <ArrowRight className="h-3 w-3 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Step 2: Revenue Tracking
// ============================================================================
function RevenueTrackingStep({ capability, onSave, onSkip, onBack }) {
  const navigate = useNavigate();
  const data = capability?.data || {};
  const [revenueModel, setRevenueModel] = useState(data.revenue_model || "");
  const [commissionReady, setCommissionReady] = useState(data.commission_ready || "");
  const [eventName, setEventName] = useState(data.event_name || "");
  const [defaultValue, setDefaultValue] = useState(data.default_value || "");
  const [checkingProfiles, setCheckingProfiles] = useState(false);
  const [profileCount, setProfileCount] = useState(null);

  const isInsurance = revenueModel === "insurance";
  const isNonInsurance = revenueModel && revenueModel !== "insurance" && revenueModel !== "subscription";

  // Check commission profiles count when insurance is selected
  const checkProfiles = useCallback(async () => {
    setCheckingProfiles(true);
    try {
      const res = await api.get("/commission/profiles");
      const count = Array.isArray(res.data) ? res.data.length : 0;
      setProfileCount(count);
      if (count > 0) {
        setCommissionReady("yes");
      }
    } catch {
      setProfileCount(0);
    } finally {
      setCheckingProfiles(false);
    }
  }, []);

  useEffect(() => {
    if (isInsurance && profileCount === null) {
      checkProfiles();
    }
  }, [isInsurance, profileCount, checkProfiles]);

  // Completion logic
  let canComplete = false;
  if (isInsurance) {
    canComplete = commissionReady === "yes" && profileCount > 0;
  } else if (isNonInsurance) {
    canComplete = eventName.trim().length > 0;
  }

  const handleSave = () => {
    const saveData = { revenue_model: revenueModel };
    if (isInsurance) {
      saveData.commission_ready = commissionReady;
      saveData.profile_count = profileCount;
    } else if (isNonInsurance) {
      saveData.event_name = eventName.trim();
      saveData.default_value = defaultValue.trim();
    }
    onSave("revenue", canComplete, saveData);
  };

  return (
    <div className="space-y-6" data-testid="revenue-step-content">
      <div>
        <p className="text-sm text-slate-300 mb-1">Revenue Tracking Configuration Required</p>
        <p className="text-xs text-slate-500">MissionAssist must know how revenue is generated to attribute performance and calculate ROI.</p>
      </div>

      {/* Revenue Model Selection */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">Revenue Model</Label>
        <div className="space-y-2">
          {REVENUE_MODELS.map((model) => (
            <button
              key={model.value}
              type="button"
              disabled={model.disabled}
              onClick={() => {
                setRevenueModel(model.value);
                setCommissionReady("");
                setProfileCount(null);
                setEventName("");
                setDefaultValue("");
              }}
              className={`w-full text-left px-4 py-3 rounded-lg border text-xs font-medium transition-all ${
                model.disabled
                  ? "bg-slate-800/30 border-slate-800 text-slate-600 cursor-not-allowed"
                  : revenueModel === model.value
                    ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                    : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
              }`}
              data-testid={`revenue-model-${model.value}`}
            >
              {model.label}
            </button>
          ))}
        </div>
      </div>

      {/* Insurance Path */}
      {isInsurance && (
        <div className="space-y-3">
          <Label className="text-xs font-medium text-slate-400">
            Are Commission Profiles configured?
          </Label>

          {checkingProfiles ? (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking Commission Profiles...
            </div>
          ) : profileCount !== null && profileCount > 0 ? (
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="revenue-profiles-found">
              <p className="text-xs text-emerald-300 font-medium">
                {profileCount} Commission Profile{profileCount !== 1 ? "s" : ""} found
              </p>
              <p className="text-[10px] text-emerald-300/60 mt-0.5">Revenue will be calculated from your existing profiles.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setCommissionReady("yes"); checkProfiles(); }}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                    commissionReady === "yes"
                      ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                      : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid="revenue-commission-yes"
                >
                  Yes, profiles are configured
                </button>
                <button
                  type="button"
                  onClick={() => setCommissionReady("not_yet")}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                    commissionReady === "not_yet"
                      ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                      : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid="revenue-commission-not-yet"
                >
                  Not yet — needs configuration
                </button>
              </div>

              {commissionReady === "not_yet" && (
                <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-3" data-testid="revenue-not-ready-block">
                  <p className="text-xs text-amber-300 font-medium">
                    Revenue tracking cannot function without Commission Profiles. This capability is required for system activation.
                  </p>
                  <div className="space-y-1.5 text-[10px] text-slate-400">
                    <p>1. Navigate to Commission Profiles</p>
                    <p>2. Create commission structures for your products</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                    onClick={() => navigate("/app/commission-setup")}
                    data-testid="revenue-setup-cta"
                  >
                    <ExternalLink className="h-3 w-3 mr-1.5" />
                    Go to Commission Profiles
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Non-Insurance Path */}
      {isNonInsurance && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="revenue-event-name" className="text-xs font-medium text-slate-400">
              Revenue Event Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="revenue-event-name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="e.g., Client Closed, Deal Won, Payment Received"
              className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
              data-testid="revenue-event-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="revenue-default-value" className="text-xs font-medium text-slate-400">
              Default Revenue Value (optional)
            </Label>
            <Input
              id="revenue-default-value"
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              placeholder="e.g., 2000"
              className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
              data-testid="revenue-default-value-input"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-xs text-slate-400 hover:text-slate-300"
            data-testid="revenue-back-btn"
          >
            <ArrowLeft className="h-3 w-3 inline mr-1" />
            Back
          </button>
          <button
            onClick={onSkip}
            className="text-xs text-slate-500 hover:text-slate-400 underline"
            data-testid="revenue-skip-btn"
          >
            Continue Without Revenue Tracking
          </button>
        </div>
        <Button
          onClick={handleSave}
          disabled={!canComplete}
          className={`text-xs ${
            canComplete
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-slate-800 text-slate-500 cursor-not-allowed"
          }`}
          data-testid="revenue-complete-btn"
        >
          <Check className="h-3 w-3 mr-1.5" />
          Complete & Continue
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Step 3: Lead Routing
// ============================================================================
function LeadRoutingStep({ capability, onSave, onSkip, onBack }) {
  const data = capability?.data || {};
  const [enabled, setEnabled] = useState(data.enabled ?? null);
  const [destinationType, setDestinationType] = useState(data.destination_type || "");
  const [provider, setProvider] = useState(data.provider || "");
  const [readiness, setReadiness] = useState(data.readiness || "");

  const isEnabled = enabled === true;
  const isDisabled = enabled === false;
  const isReady = readiness === "yes";
  const isNotReady = readiness === "not_yet";

  // If disabled, can complete immediately. If enabled, needs readiness=yes.
  const canComplete = isDisabled || Boolean(isEnabled && isReady && destinationType && provider);

  const handleSave = () => {
    const saveData = { enabled };
    if (isEnabled) {
      saveData.destination_type = destinationType;
      saveData.provider = provider;
      saveData.readiness = readiness;
    }
    onSave("lead_routing", canComplete, saveData);
  };

  return (
    <div className="space-y-6" data-testid="lead-routing-step-content">
      <div>
        <p className="text-sm text-slate-300 mb-1">Lead Routing Configuration Required</p>
        <p className="text-xs text-slate-500">Define where new leads should be sent so your team or external system can work them.</p>
      </div>

      {/* 1. Enable Lead Routing */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">
          Enable Lead Routing <span className="text-red-400">*</span>
        </Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setEnabled(true); setReadiness(""); }}
            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
              enabled === true
                ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="lead-routing-enable-yes"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setEnabled(false)}
            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
              enabled === false
                ? "bg-slate-600/20 border-slate-500/40 text-slate-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="lead-routing-enable-no"
          >
            No
          </button>
        </div>
        <p className="text-[10px] text-slate-600">
          {enabled === true
            ? "MissionAssist will send new leads to an external system for follow-up."
            : enabled === false
              ? "Leads will remain inside MissionAssist only."
              : "Choose whether leads should be routed to an external system."}
        </p>
      </div>

      {/* Conditional fields when enabled */}
      {isEnabled && (
        <>
          {/* 2. Routing Destination Type */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-400">
              Routing Destination Type <span className="text-red-400">*</span>
            </Label>
            <p className="text-[10px] text-slate-600 mb-1">Choose the type of system MissionAssist will send leads to.</p>
            <div className="flex gap-2">
              {ROUTING_DESTINATION_TYPES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDestinationType(opt.value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                    destinationType === opt.value
                      ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                      : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`lead-routing-dest-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Routing Provider */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-400">
              Routing Provider <span className="text-red-400">*</span>
            </Label>
            <p className="text-[10px] text-slate-600 mb-1">Select the system that will receive new leads.</p>
            <div className="flex flex-wrap gap-2">
              {ROUTING_PROVIDERS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setProvider(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    provider === opt.value
                      ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                      : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`lead-routing-provider-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 4. Readiness Confirmation */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-400">
              Is lead routing currently configured and active?
            </Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReadiness("yes")}
                className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                  readiness === "yes"
                    ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                    : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                }`}
                data-testid="lead-routing-readiness-yes"
              >
                Yes, routing is active
              </button>
              <button
                type="button"
                onClick={() => setReadiness("not_yet")}
                className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                  readiness === "not_yet"
                    ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                    : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                }`}
                data-testid="lead-routing-readiness-not-yet"
              >
                Not yet — needs configuration
              </button>
            </div>
          </div>

          {/* Not Ready blocking panel */}
          {isNotReady && (
            <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-3" data-testid="lead-routing-not-ready-block">
              <p className="text-xs text-amber-300 font-medium">
                Lead routing is not configured. New leads will not reach your execution system until this is completed.
              </p>
              <div className="space-y-1.5 text-[10px] text-slate-400">
                <p>1. Navigate to Settings &rarr; Lead Routing</p>
                <p>2. Connect or configure your destination</p>
                <p>3. Send a test lead to confirm delivery</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                onClick={() => window.open("/settings/lead-routing", "_blank")}
                data-testid="lead-routing-setup-cta"
              >
                <ExternalLink className="h-3 w-3 mr-1.5" />
                Go to Lead Routing Setup
              </Button>
            </div>
          )}
        </>
      )}

      {/* Disabled confirmation */}
      {isDisabled && (
        <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid="lead-routing-disabled-info">
          <p className="text-xs text-slate-400">Lead routing is disabled. Leads will be managed within MissionAssist only. You can enable this later.</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-xs text-slate-400 hover:text-slate-300"
            data-testid="lead-routing-back-btn"
          >
            <ArrowLeft className="h-3 w-3 inline mr-1" />
            Back
          </button>
          <button
            onClick={onSkip}
            className="text-xs text-slate-500 hover:text-slate-400 underline"
            data-testid="lead-routing-skip-btn"
          >
            Continue Without Lead Routing
          </button>
        </div>
        <Button
          onClick={handleSave}
          disabled={!canComplete}
          className={`text-xs ${
            canComplete
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-slate-800 text-slate-500 cursor-not-allowed"
          }`}
          data-testid="lead-routing-complete-btn"
        >
          <Check className="h-3 w-3 mr-1.5" />
          Complete & Continue
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Step 4: Appointment Sync
// ============================================================================
function AppointmentSyncStep({ capability, onSave, onSkip, onBack }) {
  const data = capability?.data || {};
  const [enabled, setEnabled] = useState(data.enabled ?? null);
  const [provider, setProvider] = useState(data.provider || "");
  const [returnMethod, setReturnMethod] = useState(data.return_method || "");
  const [readiness, setReadiness] = useState(data.readiness || "");

  const isEnabled = enabled === true;
  const isDisabled = enabled === false;
  const isNotReady = readiness === "not_yet";

  const canComplete = isDisabled || Boolean(isEnabled && readiness === "yes" && provider && returnMethod);

  const handleSave = () => {
    const saveData = { enabled };
    if (isEnabled) {
      saveData.provider = provider;
      saveData.return_method = returnMethod;
      saveData.readiness = readiness;
    }
    onSave("appointment_sync", canComplete, saveData);
  };

  return (
    <div className="space-y-6" data-testid="appointment-sync-step-content">
      <div>
        <p className="text-sm text-slate-300 mb-1">Appointment Sync Configuration Required</p>
        <p className="text-xs text-slate-500">Ensure booked appointments are returned to MissionAssist for tracking and optimization.</p>
      </div>

      {/* 1. Enable */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-400">
          Enable Appointment Tracking <span className="text-red-400">*</span>
        </Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setEnabled(true); setReadiness(""); }}
            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
              enabled === true
                ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="appt-sync-enable-yes"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setEnabled(false)}
            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
              enabled === false
                ? "bg-slate-600/20 border-slate-500/40 text-slate-300"
                : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="appt-sync-enable-no"
          >
            No
          </button>
        </div>
        <p className="text-[10px] text-slate-600">
          {enabled === true
            ? "MissionAssist will track booked appointments and downstream performance."
            : enabled === false
              ? "Appointment-level tracking and optimization will be disabled."
              : "Choose whether MissionAssist should track booked appointments."}
        </p>
      </div>

      {/* Conditional fields when enabled */}
      {isEnabled && (
        <>
          {/* 2. Booking System */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-400">
              Booking System <span className="text-red-400">*</span>
            </Label>
            <p className="text-[10px] text-slate-600 mb-1">Select the system where users book appointments.</p>
            <div className="flex flex-wrap gap-2">
              {BOOKING_PROVIDERS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setProvider(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    provider === opt.value
                      ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                      : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`appt-sync-provider-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Appointment Return Method */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-400">
              Appointment Return Method <span className="text-red-400">*</span>
            </Label>
            <p className="text-[10px] text-slate-600 mb-1">How does appointment data get back into MissionAssist?</p>
            <div className="flex flex-wrap gap-2">
              {RETURN_METHODS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setReturnMethod(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    returnMethod === opt.value
                      ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                      : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`appt-sync-method-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 4. Readiness */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-400">
              Are appointments currently being sent back to MissionAssist?
            </Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReadiness("yes")}
                className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                  readiness === "yes"
                    ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                    : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                }`}
                data-testid="appt-sync-readiness-yes"
              >
                Yes, sync is active
              </button>
              <button
                type="button"
                onClick={() => setReadiness("not_yet")}
                className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                  readiness === "not_yet"
                    ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                    : "bg-slate-800/50 border-slate-700/40 text-slate-400 hover:border-slate-600"
                }`}
                data-testid="appt-sync-readiness-not-yet"
              >
                Not yet — needs configuration
              </button>
            </div>
          </div>

          {/* Blocking panel */}
          {isNotReady && (
            <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-3" data-testid="appt-sync-not-ready-block">
              <p className="text-xs text-amber-300 font-medium">
                Appointment sync is not configured. MissionAssist cannot track booked appointments or measure performance until this is completed.
              </p>
              <div className="space-y-1.5 text-[10px] text-slate-400">
                <p>1. Navigate to Settings &rarr; Calendar Integrations</p>
                <p>2. Connect your booking system</p>
                <p>3. Configure appointment return (webhook or integration)</p>
                <p>4. Send a test appointment to verify</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                onClick={() => window.open("/settings/calendar-integrations", "_blank")}
                data-testid="appt-sync-setup-cta"
              >
                <ExternalLink className="h-3 w-3 mr-1.5" />
                Go to Appointment Sync Setup
              </Button>
            </div>
          )}
        </>
      )}

      {/* Disabled confirmation */}
      {isDisabled && (
        <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid="appt-sync-disabled-info">
          <p className="text-xs text-slate-400">Appointment tracking disabled — MissionAssist will not track booked appointments or downstream performance.</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-xs text-slate-400 hover:text-slate-300"
            data-testid="appt-sync-back-btn"
          >
            <ArrowLeft className="h-3 w-3 inline mr-1" />
            Back
          </button>
          <button
            onClick={onSkip}
            className="text-xs text-slate-500 hover:text-slate-400 underline"
            data-testid="appt-sync-skip-btn"
          >
            Continue Without Appointment Tracking
          </button>
        </div>
        <Button
          onClick={handleSave}
          disabled={!canComplete}
          className={`text-xs ${
            canComplete
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-slate-800 text-slate-500 cursor-not-allowed"
          }`}
          data-testid="appt-sync-complete-btn"
        >
          <Check className="h-3 w-3 mr-1.5" />
          Activate System
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Status Chip
// ============================================================================
function StatusChip({ status }) {
  const config = {
    activated: { label: "System Activated", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    incomplete: { label: "System Not Ready", className: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    not_ready: { label: "System Not Ready", className: "bg-red-500/15 text-red-300 border-red-500/30" },
  };
  const c = config[status] || config.not_ready;
  return (
    <Badge className={`${c.className} text-[10px] px-2 border`} data-testid="activation-status-chip">
      {c.label}
    </Badge>
  );
}

// ============================================================================
// Main Page
// ============================================================================
export default function ActivationPage() {
  const navigate = useNavigate();
  const { activation, isActivated, status, loading, updateCapability } = useActivation();

  const capabilities = activation?.capabilities || {};
  const currentStep = activation?.current_step || "intake";

  const currentStepIndex = ACTIVATION_STEPS.findIndex((s) => s.key === currentStep);
  const activeIndex = currentStepIndex >= 0 ? currentStepIndex : 0;

  // Save a capability and advance step
  const handleSave = useCallback(
    async (capKey, configured, data) => {
      const currentIdx = ACTIVATION_STEPS.findIndex((s) => s.key === capKey);
      const nextStep = ACTIVATION_STEPS[currentIdx + 1]?.key || capKey;
      await updateCapability(capKey, configured, data, configured ? nextStep : capKey);
    },
    [updateCapability]
  );

  // Skip a step: explicitly disable the capability, mark complete, advance
  const handleSkip = useCallback(
    async (capKey) => {
      const currentIdx = ACTIVATION_STEPS.findIndex((s) => s.key === capKey);
      const nextStep = ACTIVATION_STEPS[currentIdx + 1]?.key || capKey;
      // Explicitly disable: enabled=false means intentionally skipped
      await updateCapability(capKey, false, { enabled: false }, nextStep);
    },
    [updateCapability]
  );

  // Go back to a previous step
  const handleGoToStep = useCallback(
    async (stepKey) => {
      await updateCapability(stepKey, capabilities[stepKey]?.configured || false, capabilities[stepKey]?.data || {}, stepKey);
    },
    [updateCapability, capabilities]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="activation-loading">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="activation-page">
      {/* Top bar */}
      <div className="border-b border-white/[0.06] bg-slate-950/80 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/app")}
          className="text-slate-400 hover:text-white"
          data-testid="activation-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Return to App
        </Button>
        <StatusChip status={status} />
        {!isActivated && (
          <span className="text-[10px] text-red-400/70 ml-2">Execution Locked</span>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center pt-12 pb-20 px-6">
        <div className="max-w-2xl w-full">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-4">
              <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
              <span className="text-xs text-indigo-300 font-medium">System Activation</span>
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">
              {isActivated ? "System Activated" : "Activate Your System"}
            </h1>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              {isActivated
                ? "All required capabilities are configured. Execution is enabled."
                : "Required setup incomplete — MissionAssist cannot receive leads, track revenue, or execute Profit Paths until activation is complete."}
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {ACTIVATION_STEPS.map((step, idx) => {
              const cap = capabilities[step.key];
              const isComplete = cap?.configured || cap?.data?.enabled === false;
              const isCurrent = idx === activeIndex && !isActivated;
              return (
                <React.Fragment key={step.key}>
                  {idx > 0 && (
                    <div className={`h-px w-10 ${isComplete ? "bg-emerald-500/50" : "bg-slate-700"}`} />
                  )}
                  <button
                    type="button"
                    onClick={() => handleGoToStep(step.key)}
                    className="flex items-center gap-2 group"
                  >
                    <div
                      className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium border transition-all ${
                        isComplete
                          ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                          : isCurrent
                            ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300"
                            : "bg-slate-800 border-slate-700 text-slate-500"
                      }`}
                    >
                      {isComplete ? <Check className="h-3.5 w-3.5" /> : idx + 1}
                    </div>
                    <span
                      className={`text-xs font-medium transition-colors ${
                        isComplete
                          ? "text-emerald-300 group-hover:text-emerald-200"
                          : isCurrent
                            ? "text-indigo-300"
                            : "text-slate-500 group-hover:text-slate-400"
                      }`}
                    >
                      {step.label}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {/* Active Step Card */}
          {!isActivated && (
            <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 ring-1 ring-indigo-500/10 p-6" data-testid={`activation-step-${ACTIVATION_STEPS[activeIndex]?.key}`}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Circle className="h-4 w-4 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-white">
                      Step {activeIndex + 1} of {ACTIVATION_STEPS.length} — {ACTIVATION_STEPS[activeIndex]?.label}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">{ACTIVATION_STEPS[activeIndex]?.description}</p>
                  </div>
                </div>
                <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[10px]">
                  In Progress
                </Badge>
              </div>

              {/* Step Content */}
              {ACTIVATION_STEPS[activeIndex]?.key === "intake" && (
                <LeadIntakeStep
                  capability={capabilities.intake}
                  onSave={handleSave}
                  onSkip={() => handleSkip("intake")}
                />
              )}
              {ACTIVATION_STEPS[activeIndex]?.key === "revenue" && (
                <RevenueTrackingStep
                  capability={capabilities.revenue}
                  onSave={handleSave}
                  onSkip={() => handleSkip("revenue")}
                  onBack={() => handleGoToStep("intake")}
                />
              )}
              {ACTIVATION_STEPS[activeIndex]?.key === "lead_routing" && (
                <LeadRoutingStep
                  capability={capabilities.lead_routing}
                  onSave={handleSave}
                  onSkip={() => handleSkip("lead_routing")}
                  onBack={() => handleGoToStep("revenue")}
                />
              )}
              {ACTIVATION_STEPS[activeIndex]?.key === "appointment_sync" && (
                <AppointmentSyncStep
                  capability={capabilities.appointment_sync}
                  onSave={handleSave}
                  onSkip={() => handleSkip("appointment_sync")}
                  onBack={() => handleGoToStep("lead_routing")}
                />
              )}
            </div>
          )}

          {/* Completed steps (collapsed) */}
          {!isActivated && ACTIVATION_STEPS.map((step, idx) => {
            const cap = capabilities[step.key];
            if (!(cap?.configured || cap?.data?.enabled === false) || idx === activeIndex) return null;
            return (
              <div
                key={step.key}
                className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5"
                data-testid={`activation-step-${step.key}-complete`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-white">{step.label}</h3>
                      <p className="text-xs text-slate-500">{step.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
                      Ready
                    </Badge>
                    <button
                      onClick={() => handleGoToStep(step.key)}
                      className="text-[10px] text-slate-500 hover:text-slate-400 underline"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Activated state */}
          {isActivated && (
            <div className="text-center" data-testid="activation-complete">
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-8 mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/25 mb-4">
                  <Check className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300 font-medium">System is ready for execution</span>
                </div>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  All required capabilities are configured. You can now create and approve Profit Paths for execution.
                </p>

                {/* Show completed steps summary */}
                <div className="mt-6 space-y-2 max-w-sm mx-auto">
                  {ACTIVATION_STEPS.map((step) => {
                    const cap = capabilities[step.key];
                    return (
                      <div key={step.key} className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                        <div className="flex items-center gap-2">
                          <Check className="h-3 w-3 text-emerald-400" />
                          <span className="text-xs text-emerald-300">{step.label}</span>
                        </div>
                        <button
                          onClick={() => handleGoToStep(step.key)}
                          className="text-[10px] text-slate-500 hover:text-slate-400 underline"
                        >
                          Edit
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button
                onClick={() => navigate("/app/profit-paths")}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="activation-go-to-paths-btn"
              >
                Go to Profit Paths
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
